import { type ReactNode, useEffect, useState } from "react";
// @ts-ignore
import { reset } from "sync-scroll";
import "sync-scroll";
import { useDispatch, useSelector } from "react-redux";
import ProductComparisonCard from "~/features/compareProducts/components/ProductComparisonCard";
import ProductComparisonSmallCard from "~/features/compareProducts/components/ProductComparisonSmallCard";
import { useOnMount } from "~/common/hooks/useOnMount";
import { type PdpPageProductData } from "~/features/productDetails/models/PdpPageProductData";
import EmptyComparisonPage from "~/features/compareProducts/components/EmptyComparison";
import ComercialTable from "~/features/compareProducts/components/ComercialTable";
import AvailabilityTable from "~/features/compareProducts/components/AvailabilityTable";
import ProductInformationTable from "~/features/compareProducts/components/ProductInformationTable";
import { userIsDistributor } from "~/domains/user/utils/roles";
import { selectProductComparisonList } from "~/features/compareProducts/redux/productComparisonSelectors";
import { addBaseProductDataAction, requestAvailabilityData, requestPriceData } from "~/domains/product/redux/actions";
import { type RootState } from "~/app/store";
import { addToComparisonBulk, clearComparisonList } from "~/features/compareProducts/redux/actions";
import {
  trackAddToComparisonClick,
  trackClearComparisonClick,
  trackPCDownload,
  trackProductComparisonPageNavigation,
} from "~/features/compareProducts/utils/analytics";
import { selectProductDescriptor } from "~/domains/product/redux/selectors";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { ReduxImperialToggle } from "~/domains/userPreferences/components/UnitModeToggle";
import { IncludesSurchargeDisclaimer } from "~/domains/pricing/components/IncludesSurchargeDisclaimer";
import { type PdpPageProductDataDto } from "~/features/productDetails/dto/PdpPageProductDataDto";
import { transformPdpProduct } from "~/features/productDetails/transformers/PdpPageProductDataTransformer";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useUnitMode } from "~/domains/userPreferences/hooks/useUnitMode";
import { downloadPC } from "~/features/compareProducts/api/DownloadFile";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { DownloadClickLink } from "~/common/components/Link";
import { QuickEntryPopup } from "~/features/quickEntry/v2/QuickEntryPopup";
import { Trans, useTranslation } from "react-i18next";
import { hasProductConfiguredPC } from "~/features/quickEntry/utils/helpers";
import { addMessage } from "~/features/toasts/redux/actions";
import {
  type AllScreenSizes,
  SIZE_BELOW_PHONE,
  SIZE_DESKTOP_L,
  SIZE_DESKTOP_M,
  SIZE_DESKTOP_S,
  SIZE_PHONE,
  SIZE_TABLET,
  useNumericScreenSize,
} from "~/common/hooks/useNumericScreenSize";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { getStore } from "~/app/store/getStore";
import { Alert } from "~/common/mui/Alert/Alert";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { styled } from "~/common/mui";

interface Props {
  data: PdpPageProductData[];
}

const ProductComparisonPage = ({ data }: Props) => {
  const { t } = useTranslation();
  const { maxSize } = window.SCHA.productComparisonList;

  const size = useNumericScreenSize();
  const isDesktop = size >= SIZE_DESKTOP_S;

  const [renderChildren, setRenderChildren] = useState(false);
  const [scrollAtCards, setScrollAtCards] = useState(false);
  const { val: statusModal, setTrue: openModal, setFalse: closeModal } = useBooleanState(false);
  const productCodes = useSelector(selectProductComparisonList);
  const unitMode = useUnitMode();

  const productDescriptorList: ProductDescriptor<PdpPageProductData>[] = useSelector((state: RootState) => {
    return productCodes.map(
      (productCode) =>
        selectProductDescriptor(state, {
          productCode,
          priceReference: getInitialPriceReference(),
        }) as ProductDescriptor<PdpPageProductData>,
    );
  });

  const pageContainsSurcharge =
    productDescriptorList.length > 0 &&
    productDescriptorList.some(
      (product) => product.prices.length === 1 && (product.prices[0].surchargeScale[0]?.surcharge ?? 0) > 0,
    );

  const pageContainsTariffSurcharge =
    productDescriptorList.length > 0 &&
    productDescriptorList.some(
      (product) => product.prices.length === 1 && product.prices[0].isTariffSurchargeApplicable,
    );

  useEffect(() => {
    const yVal = 210;
    const listenScrollEvent = () => {
      setScrollAtCards(window.scrollY > yVal);
    };
    window.addEventListener("scroll", listenScrollEvent);
    return () => window.removeEventListener("scroll", listenScrollEvent);
  }, [isDesktop]);

  const productDesignations = productDescriptorList.map((descriptor) => descriptor.base.designation);

  const dispatch = useDispatch();
  const handleRemoveAllClick = () => {
    dispatch(clearComparisonList());
    trackClearComparisonClick(productDesignations);
  };

  useOnMount(() => trackProductComparisonPageNavigation(productDesignations));

  useOnMount(() => {
    data.forEach((product) => {
      if (userHasPermission(Permission.PRICE)) {
        dispatch(requestPriceData(product.code, getInitialPriceReference()));
      }
      if (userHasPermission(Permission.AVAILABILITY)) {
        dispatch(requestAvailabilityData(product.code));
      }
    });
    setRenderChildren(true);
  });

  const placeholders = Array.from({ length: maxSize - productCodes.length }, (_, idx) => productCodes.length + idx);

  const tables = document.querySelectorAll("table");
  tables.forEach((table, idx) => {
    if (idx % 2 === 0 || idx % 2 === 1) {
      table?.parentElement?.classList.add("remove-scroll");
      table?.parentElement?.parentElement?.classList.add("syncscroll");
      table?.parentElement?.parentElement?.setAttribute("name", "myElements");
    }
    reset();
  });

  const productDescriptors = useSelector((state: RootState) => {
    return productCodes.map((productCode) => selectProductDescriptor(state, { productCode, priceReference: "" }));
  });

  const downloadFunc = (type: "PDF" | "XLSX") => {
    trackPCDownload(type, productDesignations);
    downloadPC(type, unitMode);
  };

  const colSize = COL_SIZES[size];

  useEffect(() => {
    reset();
  }, [size, ...productDescriptors]);

  const quickEntryPopup = (
    <QuickEntryPopup
      onClose={closeModal}
      maxAmount={maxSize - productCodes.length}
      validateRow={(row, itemsPopup = []) => {
        const errors: ReactNode[] = [];
        const filteredItemsPopup = itemsPopup
          .filter((item) => item.id !== row.id)
          .map(function (item) {
            return item.productId;
          });
        const dataPagePlusPopup = [...productDesignations, ...filteredItemsPopup];
        if (dataPagePlusPopup.includes(row.product.designation)) {
          errors.push(
            <Alert className="product-id" severity={"error"}>
              <Trans i18nKey={"bulkUpload.productComparison.error.repeatedProduct"}>
                The product <span>{{ designation: row.product.designation }}</span> is already in comparison list.
                Choose different product to compare.
              </Trans>
            </Alert>,
          );
        }
        return errors;
      }}
      onAdd={async (form, setIsSending, onClose) => {
        const items = form.items.filter(hasProductConfiguredPC);
        const arrProd = items.map((item) => item.product.code);
        setIsSending(true);
        await dispatch(addToComparisonBulk(arrProd));
        const designations = items.map((item) => item.product.designation);
        trackAddToComparisonClick(designations, true, "Product Comparison");
        setIsSending(false);
        if (arrProd.length > 0) {
          dispatch(addMessage("info", t("addToPC.success", { count: arrProd.length })));
        }
        onClose();
      }}
      addButtonLabel={t("comparisonPage.modal.add.submitBtn")}
    />
  );

  return (
    <ComparisonPageRoot className={"scha__comparison-page__root"}>
      {productCodes.length > 0 ? (
        <>
          <ComparisonPageTitle
            className="scha__comparison-page__header"
            title={t("comparisonPage.title", { length: productCodes.length, maxSize })}
          />
          {/* @ts-ignore */}
          <CardsContainer className="syncscroll scha__comparison-page__cards-container" name="myElements">
            <div className={scrollAtCards ? "cards-area-scrolled" : "cards-area"}>
              <ClearListArea className={"scha__comparison-page__clear-list-area"}>
                <div className={"scha__comparison-page__imperial"}>
                  <ReduxImperialToggle className={"scha__comparison-page__imperial-toggle"} page="Comparison List" />
                </div>
                <OptionsArea className={"scha__comparison-page__options"}>
                  <div className={"d-flex flex-direction-column"}>
                    <DownloadTitle className="scha__comparison-page__download-title">
                      {t("comparisonPage.downloadFile.title")}
                    </DownloadTitle>
                    <DownloadContainer className="scha__comparison-page__download-container">
                      <DownloadClickLink onClick={() => downloadFunc("PDF")}>
                        {t("downloadFile.type.pdf")}
                      </DownloadClickLink>
                      <DownloadClickLink onClick={() => downloadFunc("XLSX")}>
                        {t("downloadFile.type.excel")}
                      </DownloadClickLink>
                    </DownloadContainer>
                  </div>

                  <ClearListButton className="scha__comparison-page__clear-list" onClick={handleRemoveAllClick}>
                    <TrashIcon className="scha__comparison-page__trash-icon"  />
                    {t("comparisonPage.clear.list")}
                  </ClearListButton>
                </OptionsArea>
              </ClearListArea>
              <div className="d-flex cards-list">
                {renderChildren &&
                  productCodes.map((code) =>
                    scrollAtCards ? (
                      <ProductComparisonSmallCard key={code} productCode={code} />
                    ) : (
                      <ProductComparisonCard key={code} productCode={code} />
                    ),
                  )}
                {placeholders.map((items, index) => (
                  <>
                    {index === 0 ? (
                      <AddCard className="scha__comparison-page__add-card" key={index} onClick={openModal}>
                        <div className={"scha__comparison-page__add-card__icon"}>
                          <AddIcon sx={{ fontSize: isDesktop ? 24 : 20 }} />
                        </div>
                        <h1 className={"scha__comparison-page__add-card__title"}>
                          {t("comparisonPage.addProduct.title")}
                        </h1>
                      </AddCard>
                    ) : (
                      <EmptyCard className="scha__comparison-page__empty-card" key={index} />
                    )}
                  </>
                ))}
              </div>
            </div>
          </CardsContainer>
          <TableContainer className="scha__comparison-page__table-container">
            {/* @ts-ignore */}
            {userIsDistributor() && (
              <>
                <ComercialTable products={productDescriptorList} placeholders={placeholders} colSize={colSize} />
                <AvailabilityTable products={productDescriptorList} placeholders={placeholders} colSize={colSize} />
              </>
            )}
            <ProductInformationTable products={productDescriptorList} placeholders={placeholders} colSize={colSize} />
          </TableContainer>
          <Disclaimers className="scha__comparison-page__disclaimers">
            <Alert severity={"info"}>{t("comparisonPage.selectUnitType")}</Alert>
            {
              <IncludesSurchargeDisclaimer
                hasSurcharge={pageContainsSurcharge}
                tariffSurcharge={pageContainsTariffSurcharge}
              />
            }
          </Disclaimers>
        </>
      ) : (
        <EmptyComparisonPage openQuickEntry={openModal} />
      )}
      {statusModal && quickEntryPopup}
    </ComparisonPageRoot>
  );
};

export function createProductComparisonPage({ json }: { json: string }) {
  let products: PdpPageProductData[];
  if (json) {
    products = (JSON.parse(json) as PdpPageProductDataDto[]).map((item) => transformPdpProduct(item, null));
    getStore().dispatch(addBaseProductDataAction(products));
  } else {
    products = [];
  }
  return <ProductComparisonPage data={products} />;
}

type SizeMap = Record<AllScreenSizes, number>;
const COL_SIZES: SizeMap = {
  [SIZE_BELOW_PHONE]: 248,
  [SIZE_PHONE]: 248,
  [SIZE_TABLET]: 248,
  [SIZE_DESKTOP_S]: 330,
  [SIZE_DESKTOP_M]: 378,
  [SIZE_DESKTOP_L]: 304,
};

const ComparisonPageRoot = styled("div")(({ theme }) => ({
  "--cell-width": "248px",
  "--card-width": "224px",
  [theme.breakpoints.up("desktop-s")]: {
    "--cell-width": "330px",
    "--card-width": "306px",
  },
  [theme.breakpoints.up("desktop-m")]: {
    "--cell-width": "378px",
    "--card-width": "354px",
  },
  [theme.breakpoints.up("desktop-l")]: {
    "--cell-width": "304px",
    "--card-width": "272px",
  },
}));

const ComparisonPageTitle = styled(PageTitle)(({ theme }) => ({
  ...theme.typography.titleLarge,
  color: theme.palette.text.primary,
  [theme.breakpoints.up("desktop-s")]: {
    ...theme.typography.headlineMedium,
  },
}));

const CardsContainer = styled("div")(({ theme }) => ({
  display: "grid",
  position: "sticky",
  top: 0,
  zIndex: 999,
  backgroundColor: theme.palette.common.white,
  overflowX: "scroll",
  marginTop: theme.spacing(3.75),
  "&::-webkit-scrollbar": { height: theme.spacing(1) },
  "&::-webkit-scrollbar-track": { background: theme.palette.surface.main, borderRadius: theme.spacing(0.5) },
  "&::-webkit-scrollbar-thumb": { background: theme.palette.text.secondary, borderRadius: theme.spacing(0.5) },
  [theme.breakpoints.up("desktop-l")]: {
    overflowX: "initial",
  },
  "& .cards-area": {
    display: "flex",
    [theme.breakpoints.down("tablet")]: {
      flexDirection: "column",
      position: "initial",
    },
  },
  "& .cards-area-scrolled": {
    display: "flex",
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(2.5),
    paddingTop: theme.spacing(1.25),
    [theme.breakpoints.down("tablet")]: {
      flexDirection: "column",
    },
  },
  [theme.breakpoints.down("tablet")]: {
    marginTop: theme.spacing(2.5),
    flexDirection: "column",
  },
}));

const ClearListArea = styled("div")(({ theme }) => ({
  position: "sticky",
  left: 0,
  zIndex: 9999,
  backgroundColor: theme.palette.common.white,
  width: 328,
  [theme.breakpoints.up("tablet")]: {
    width: 224,
    borderRight: `1px solid ${theme.palette.divider}`,
  },
  "& .scha__comparison-page__imperial-toggle": {
    marginBottom: theme.spacing(2.5),
    paddingRight: theme.spacing(2),
  },
}));

const OptionsArea = styled("div")(({ theme }) => ({
  width: "100%",
  display: "flex",
  flexDirection: "column",
  [theme.breakpoints.down("tablet")]: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
}));

const DownloadTitle = styled("div")(({ theme }) => ({
  ...theme.typography.labelLarge,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

const DownloadContainer = styled("div")(({ theme }) => ({
  display: "flex",
  marginBottom: theme.spacing(2.5),
  gap: theme.spacing(3),
}));

const ClearListButton = styled("div")(({ theme }) => ({
  display: "flex",
  color: theme.palette.primary.main,
  cursor: "pointer",
  ...theme.typography.labelLarge,
  "&:hover": {
    textDecoration: "underline",
  },
  [theme.breakpoints.down("desktop-s")]: {
    marginBottom: theme.spacing(2.5),
  },
}));

const TrashIcon = styled(DeleteIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
}));

const cardMarginStyles = {
  width: "var(--card-width)",
  "--margin-size": "calc((var(--cell-width) - var(--card-width)) / 2)",
  marginLeft: "var(--margin-size)",
  marginRight: "var(--margin-size)",
};

const EmptyCard = styled("div")(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  ...cardMarginStyles,
}));

const AddCard = styled("div")(({ theme }) => ({
  border: `1px dashed ${theme.palette.divider}`,
  borderRadius: theme.spacing(0.5),
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  ...cardMarginStyles,
  "&__icon, & .scha__comparison-page__add-card__icon": {
    padding: theme.spacing(2.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "50%",
    width: theme.spacing(8),
    height: theme.spacing(8),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  "&__title, & .scha__comparison-page__add-card__title": {
    ...theme.typography.headlineMedium,
    textAlign: "center",
  },
  "&:hover": {
    cursor: "pointer",
    background: theme.palette.surface.main,
    "& .scha__comparison-page__add-card__icon, & .scha__comparison-page__add-card__title": {
      color: theme.palette.primary.main,
    },
  },
  [theme.breakpoints.down("tablet")]: {
    "&__icon, & .scha__comparison-page__add-card__icon": {
      width: theme.spacing(6),
      height: theme.spacing(6),
      padding: theme.spacing(1.6875),
    },
    "&__title, & .scha__comparison-page__add-card__title": {
      ...theme.typography.titleMedium,
    },
  },
}));

const TableContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  marginBottom: theme.spacing(2.5),
  '& [class*="horizontalScrollContainer"] ::-webkit-scrollbar-thumb': {
    backgroundColor: "transparent !important",
  },
  "& > div": {
    "&::-webkit-scrollbar": { height: theme.spacing(1) },
    "&::-webkit-scrollbar-track": { background: theme.palette.surface.main, borderRadius: theme.spacing(0.5) },
    "&::-webkit-scrollbar-thumb": { background: theme.palette.text.secondary, borderRadius: theme.spacing(0.5) },
  },
}));

const Disclaimers = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(3.75),
}));
